import api from './api';

/**
 * Education Service
 * 
 * Service for managing education modules and user progress
 */

export interface EducationModule {
  id: string;
  module_code: string;
  title: string;
  description: string;
  content?: {
    sections: Array<{
      title: string;
      content: string;
    }>;
  };
  target_roles?: string[];
  sort_order: number;
  is_active: boolean;
  progress_percentage?: number;
  completed_at?: string;
  started_at?: string;
  last_accessed_at?: string;
}

export interface EducationProgressSummary {
  total_modules: number;
  started_modules: number;
  completed_modules: number;
  average_progress: number;
}

export interface ModuleStats {
  title: string;
  total_users: number;
  completed_users: number;
  average_progress: number;
  active_last_week: number;
}

class EducationService {
  /**
   * Get education modules for a user
   */
  async getEducationModules(userId: string, role?: string): Promise<EducationModule[]> {
    try {
      const params = role ? { role } : {};
      const response = await api.get(`/education/user/${userId}/modules`, { params });
      
      if (response.data.success) {
        return response.data.modules || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching education modules:', error);
      throw error;
    }
  }

  /**
   * Get education module content
   */
  async getModuleContent(moduleId: string, userId?: string): Promise<EducationModule | null> {
    try {
      if (!moduleId) {
        throw new Error('Module ID is required');
      }
      const params = userId ? { userId } : {};
      const response = await api.get(`/education/module/${encodeURIComponent(moduleId)}/content`, { params });
      
      if (response.data.success) {
        return response.data.module || null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching module content:', error);
      throw error;
    }
  }

  /**
   * Update module progress
   */
  async updateModuleProgress(
    moduleId: string,
    userId: string,
    progressPercentage: number,
    completed?: boolean
  ): Promise<void> {
    try {
      await api.put(`/education/module/${moduleId}/progress`, {
        userId,
        progressPercentage,
        completed: completed || false
      });
    } catch (error) {
      console.error('Error updating module progress:', error);
      throw error;
    }
  }

  /**
   * Get user's education progress summary
   */
  async getUserProgressSummary(userId: string): Promise<EducationProgressSummary | null> {
    try {
      const response = await api.get(`/education/user/${userId}/summary`);
      
      if (response.data.success) {
        return response.data.summary || null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching progress summary:', error);
      throw error;
    }
  }

  /**
   * Get module statistics (for admins)
   */
  async getModuleStats(moduleId: string): Promise<ModuleStats | null> {
    try {
      const response = await api.get(`/education/module/${moduleId}/stats`);
      
      if (response.data.success) {
        return response.data.stats || null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching module stats:', error);
      throw error;
    }
  }
}

export default new EducationService();

